/**
 * Chart Layout Manager
 * Manages multi-chart layouts with localStorage persistence
 */

import { LayoutType, LayoutConfig, ChartPanelConfig } from '../types/chartLayout';

type LayoutEventType = 'layout-changed' | 'panel-updated' | 'sync-changed';
type LayoutEventCallback = (layout: LayoutConfig) => void;

const STORAGE_KEY = 'tradevision_chart_layout_v1';
const DEFAULT_SYMBOL = 'AAPL';
const DEFAULT_TIMEFRAME = '1m';

export class ChartLayoutManager {
  private layout: LayoutConfig;
  private listeners: Map<LayoutEventType, Set<LayoutEventCallback>>;

  constructor() {
    this.listeners = new Map();
    this.layout = this.loadLayout() || this.createDefaultLayout('1x1');
  }

  /**
   * Get current layout configuration
   */
  getLayout(): LayoutConfig {
    return { ...this.layout };
  }

  /**
   * Get specific panel by ID
   */
  getPanel(panelId: string): ChartPanelConfig | undefined {
    return this.layout.panels.find(p => p.id === panelId);
  }

  /**
   * Change layout type
   * @param layoutType - The new layout type
   * @param preserveSymbols - Whether to preserve symbols from old panels
   * @param currentSymbol - Optional current symbol from main chart (used when switching from 1x1)
   * @param currentTimeframe - Optional current timeframe from main chart (used when switching from 1x1)
   */
  changeLayout(layoutType: LayoutType, preserveSymbols: boolean = true, currentSymbol?: string, currentTimeframe?: string): void {
    const oldPanels = this.layout.panels;
    const newLayout = this.createDefaultLayout(layoutType);

    // Preserve symbols and timeframes if requested
    if (preserveSymbols && oldPanels.length > 0) {
      // Use the current symbol/timeframe from main chart if provided, otherwise use first panel's values
      // This ensures the actual displayed timeframe is used, not the stale localStorage value
      const baseSymbol = currentSymbol || oldPanels[0].symbol;
      const baseTimeframe = currentTimeframe || oldPanels[0].timeframe;
      
      
      newLayout.panels.forEach((panel, index) => {
        if (oldPanels[index] && !currentTimeframe) {
          // Preserve existing panel's symbol/timeframe only if no override provided
          panel.symbol = oldPanels[index].symbol;
          panel.timeframe = oldPanels[index].timeframe;
        } else {
          // For new panels or when override provided, use the base symbol/timeframe
          panel.symbol = baseSymbol;
          panel.timeframe = baseTimeframe;
        }
      });
    }

    // Preserve sync settings
    newLayout.syncCrosshair = this.layout.syncCrosshair;
    newLayout.syncTimeframe = this.layout.syncTimeframe;
    newLayout.syncSymbol = this.layout.syncSymbol;

    this.layout = newLayout;
    this.saveLayout();
    this.emit('layout-changed', this.layout);
  }

  /**
   * Update panel symbol
   */
  updatePanelSymbol(panelId: string, symbol: string): void {
    const panel = this.layout.panels.find(p => p.id === panelId);
    if (!panel) return;

    panel.symbol = symbol;

    // If symbol sync is enabled, update all panels
    if (this.layout.syncSymbol) {
      this.layout.panels.forEach(p => {
        p.symbol = symbol;
      });
    }

    this.saveLayout();
    this.emit('panel-updated', this.layout);
  }

  /**
   * Update panel timeframe
   */
  updatePanelTimeframe(panelId: string, timeframe: string): void {
    const panel = this.layout.panels.find(p => p.id === panelId);
    if (!panel) return;

    panel.timeframe = timeframe;

    // If timeframe sync is enabled, update all panels
    if (this.layout.syncTimeframe) {
      this.layout.panels.forEach(p => {
        p.timeframe = timeframe;
      });
    }

    this.saveLayout();
    this.emit('panel-updated', this.layout);
  }

  /**
   * Update panel configuration
   */
  updatePanel(panelId: string, updates: Partial<ChartPanelConfig>): void {
    const panel = this.layout.panels.find(p => p.id === panelId);
    if (!panel) return;

    Object.assign(panel, updates);
    this.saveLayout();
    this.emit('panel-updated', this.layout);
  }

  /**
   * Update all panels with the same configuration (batch update)
   * Used when loading chart from home screen to sync all panels
   */
  updateAllPanels(updates: Partial<ChartPanelConfig>): void {
    this.layout.panels.forEach(panel => {
      Object.assign(panel, updates);
    });
    this.saveLayout();
    this.emit('panel-updated', this.layout);
  }

  /**
   * Toggle sync options
   */
  toggleSync(syncType: 'crosshair' | 'timeframe' | 'symbol'): void {
    switch (syncType) {
      case 'crosshair':
        this.layout.syncCrosshair = !this.layout.syncCrosshair;
        break;
      case 'timeframe':
        this.layout.syncTimeframe = !this.layout.syncTimeframe;
        // If enabling sync, sync all panels to first panel's timeframe
        if (this.layout.syncTimeframe && this.layout.panels.length > 0) {
          const firstTimeframe = this.layout.panels[0].timeframe;
          this.layout.panels.forEach(p => {
            p.timeframe = firstTimeframe;
          });
        }
        break;
      case 'symbol':
        this.layout.syncSymbol = !this.layout.syncSymbol;
        // If enabling sync, sync all panels to first panel's symbol
        if (this.layout.syncSymbol && this.layout.panels.length > 0) {
          const firstSymbol = this.layout.panels[0].symbol;
          this.layout.panels.forEach(p => {
            p.symbol = firstSymbol;
          });
        }
        break;
    }

    this.saveLayout();
    this.emit('sync-changed', this.layout);
  }

  /**
   * Export layout as JSON
   */
  exportLayout(): string {
    return JSON.stringify(this.layout, null, 2);
  }

  /**
   * Import layout from JSON
   */
  importLayout(layoutJson: string): void {
    try {
      const imported = JSON.parse(layoutJson) as LayoutConfig;
      
      // Validate layout
      if (!imported.type || !imported.panels || !Array.isArray(imported.panels)) {
        throw new Error('Invalid layout format');
      }

      this.layout = imported;
      this.saveLayout();
      this.emit('layout-changed', this.layout);
    } catch (error) {
      console.error('[ChartLayoutManager] Failed to import layout:', error);
      throw error;
    }
  }

  /**
   * Reset to default layout
   */
  reset(): void {
    this.layout = this.createDefaultLayout('1x1');
    this.saveLayout();
    this.emit('layout-changed', this.layout);
  }

  /**
   * Subscribe to layout events
   */
  subscribe(event: LayoutEventType, callback: LayoutEventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Emit event to all listeners
   */
  private emit(event: LayoutEventType, layout: LayoutConfig): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(layout));
    }
  }

  /**
   * Create default layout for given type
   */
  private createDefaultLayout(type: LayoutType): LayoutConfig {
    const panelCount = this.getPanelCount(type);
    const panels: ChartPanelConfig[] = [];

    for (let i = 0; i < panelCount; i++) {
      panels.push({
        id: `panel-${i}`,
        symbol: DEFAULT_SYMBOL,
        timeframe: DEFAULT_TIMEFRAME
      });
    }

    return {
      type,
      panels,
      syncCrosshair: false,
      syncTimeframe: false,
      syncSymbol: false
    };
  }

  /**
   * Get number of panels for layout type
   */
  private getPanelCount(type: LayoutType): number {
    const counts: Record<LayoutType, number> = {
      '1x1': 1,
      '2x1': 2,
      '1x2': 2,
      '2x2': 4,
      '3x1': 3,
      '1x3': 3,
      '3x2': 6,
      '2x3': 6,
      '4x1': 4,
      '1x4': 4
    };
    return counts[type] || 1;
  }

  /**
   * Save layout to localStorage
   */
  private saveLayout(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.layout));
    } catch (error) {
      console.error('[ChartLayoutManager] Failed to save layout:', error);
    }
  }

  /**
   * Load layout from localStorage
   */
  private loadLayout(): LayoutConfig | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return null;
      
      const layout = JSON.parse(saved) as LayoutConfig;
      
      // Validate loaded layout
      if (!layout.type || !layout.panels || !Array.isArray(layout.panels)) {
        console.warn('[ChartLayoutManager] Invalid saved layout, using default');
        return null;
      }

      // Ensure all panels have valid symbols (fix for empty symbols from old data)
      layout.panels.forEach(panel => {
        if (!panel.symbol || panel.symbol.trim().length === 0) {
          console.warn(`[ChartLayoutManager] Panel ${panel.id} has empty symbol, setting to default`);
          panel.symbol = DEFAULT_SYMBOL;
        }
        if (!panel.timeframe || panel.timeframe.trim().length === 0) {
          panel.timeframe = DEFAULT_TIMEFRAME;
        }
      });

      return layout;
    } catch (error) {
      console.error('[ChartLayoutManager] Failed to load layout:', error);
      return null;
    }
  }
}

// Singleton instance
let layoutManagerInstance: ChartLayoutManager | null = null;

/**
 * Get singleton layout manager instance
 */
export function getLayoutManager(): ChartLayoutManager {
  if (!layoutManagerInstance) {
    layoutManagerInstance = new ChartLayoutManager();
  }
  return layoutManagerInstance;
}

/**
 * Reset layout manager instance (for testing)
 */
export function resetLayoutManager(): void {
  layoutManagerInstance = null;
}
